import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { QuotaReport } from '../api/types'

interface Props {
  propertyId?: string
}

/**
 * Today's GA4 token spend.
 *
 * GA4 reports quota inline on every response, so this reads whatever the last request
 * already brought back and never spends a token to do it. Nothing here is fetched on a
 * timer for the same reason a briefing is not: the numbers only move when something
 * else asks GA4 a question.
 *
 * The daily figure is measured downward from the highest balance seen rather than
 * summed upward from zero, so a restart mid-window loses only the part of the day it
 * did not witness — and says so, rather than reporting the floor as a total.
 */
export function QuotaPanel({ propertyId }: Props) {
  const [report, setReport] = useState<QuotaReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    api
      .quota({ propertyId }, controller.signal)
      .then(setReport)
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError((cause as Error).message)
      })
    return () => controller.abort()
  }, [propertyId])

  if (error) return <p className="placeholder">Quota unavailable — {error}</p>
  if (!report) return <p className="placeholder">Loading quota…</p>

  const { daily } = report
  const nothingFetched = daily.remaining === 0 && daily.used === 0

  if (nothingFetched) {
    return (
      <div className="card">
        <p className="placeholder">
          No GA4 request has been made yet in this process, so there is no quota to report.
        </p>
      </div>
    )
  }

  const percent = daily.limit ? Math.min((daily.used / daily.limit) * 100, 100) : null

  return (
    <div className="card">
      <div className="tiles">
        <div className="tile">
          <span className="tile__label">Tokens used today</span>
          <span className="tile__value">{daily.used.toLocaleString()}</span>
          <span className="tile__delta-note">
            {daily.since_reset
              ? `of ${daily.limit?.toLocaleString()} in the daily bucket`
              : 'at least — this service started mid-window'}
          </span>
        </div>
        <div className="tile">
          <span className="tile__label">Remaining</span>
          <span className="tile__value">{daily.remaining.toLocaleString()}</span>
          <span className="tile__delta-note">as GA4 reported it on the last request</span>
        </div>
        <div className="tile">
          <span className="tile__label">Last request cost</span>
          <span className="tile__value">{report.last_request_tokens.toLocaleString()}</span>
          <span className="tile__delta-note">
            {report.tokens_used_this_process.toLocaleString()} since this process started
          </span>
        </div>
      </div>

      {percent !== null && (
        <div
          className="quota-bar"
          role="meter"
          aria-valuenow={daily.used}
          aria-valuemin={0}
          aria-valuemax={daily.limit ?? undefined}
          aria-label="Daily GA4 token quota used"
        >
          <div className="quota-bar__fill" style={{ width: `${percent}%` }} />
        </div>
      )}

      {!daily.since_reset && (
        <p className="section__note" style={{ marginTop: 10 }}>
          The daily bucket resets on Google&rsquo;s clock, not this one. Until a reset has
          been observed the total above is a floor, and the ceiling is unknown — anything
          spent before this service started is not counted.
        </p>
      )}
    </div>
  )
}
